/** @format */
import { Component, DestroyRef, Inject, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StateService } from '@uirouter/angular';
import { ValidatablePlan } from 'common-typescript';
import { CourseUnit, OtmId } from 'common-typescript/types';
import { BehaviorSubject, mergeMap, Observable, take } from 'rxjs';
import { PLAN_ACTIONS_SERVICE_INJECTION_TOKEN, PlanActionsService } from 'sis-components/plan/plan-actions-service/plan-actions.service';
import { PlanManager } from 'sis-components/plan/plan-manager/plan-manager.service';
import { PlanEntityService } from 'sis-components/service/plan-entity.service';
import { StudentPlanActionsService } from '../../common/service/student-plan-actions.service';
import { PlanStructureData } from '../plan-data.service';
import { STUDENT_PLAN_STRUCTURE } from '../plan-ng.route.constant';
import { createAddToPlanWizardStateService } from './store/add-to-plan-wizard-utils';

@Component({
    selector: 'app-add-to-plan-wizard',
    standalone: false,
    templateUrl: './add-to-plan-wizard.component.html',
    providers: [
        {
            provide: PLAN_ACTIONS_SERVICE_INJECTION_TOKEN,
            useClass: StudentPlanActionsService,
        },
        PlanManager,
    ],
    encapsulation: ViewEncapsulation.None,
})
export class AddToPlanWizardComponent implements OnInit {
    @Input() courseUnit: CourseUnit;
    @Input() planId: OtmId;

    wizardProgressStepKeys = ['ADD_TO_PLAN_WIZARD.CHOOSE_DESTINATION', 'ADD_TO_PLAN_WIZARD.PREVIEW_AND_CONFIRM'];
    progressStep = new BehaviorSubject<number>(0);
    wizardStateService = createAddToPlanWizardStateService();
    data$: Observable<void>;
    validatablePlan: ValidatablePlan;

    constructor(
        private readonly stateService: StateService,
        private readonly planEntityService: PlanEntityService,
        @Inject(PLAN_ACTIONS_SERVICE_INJECTION_TOKEN) private readonly planActionsService: PlanActionsService,
        private readonly planManager: PlanManager,
        private readonly destroyRef: DestroyRef,
    ) {}
    ngOnInit() {
        this.createPlanOperationSubjectSubscription();
    }

    next() {
        this.progressStep.next(this.progressStep.value + 1);
    }

    previous() {
        this.progressStep.next(this.progressStep.value - 1);
    }

    initPlanManager(currentData: PlanStructureData) {
        this.planManager.setValidatablePlan(currentData.validatablePlan);
        this.planManager.setStudyRight(currentData.validatablePlan.studyRight);
        this.planManager.validatablePlanSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(newValidatablePlan => {
            this.validatablePlan = newValidatablePlan;
        });
    }
    setPlan(currentData: PlanStructureData): void {
        this.planManager.setValidatablePlan(currentData.validatablePlan);
        this.planManager.setStudyRight(currentData.validatablePlan.studyRight);
    }
    /**
     * Subscribes to planOperationSubject and processes the operations by passing them directly to planManager.
     */
    createPlanOperationSubjectSubscription(): void {
        this.planActionsService.planOperationSubject
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                mergeMap(operation => this.planManager.processPlanOperation(operation)),
            )
            .subscribe();
    }

    confirm() {
        const plan = this.planManager.validatablePlan.plan;
        const planId = plan.id;
        this.planEntityService
            .updateMyPlan(plan)
            .pipe(take(1))
            .subscribe(() => this.stateService.go(STUDENT_PLAN_STRUCTURE, { planId }));
    }
}
